/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { mixin } from 'lodash';
import { parallelLimit } from 'async';
import Vue from 'vue';

import { ItemTypeAPIs, ItemTypeConsts } from 'client/util/Constants';
import CollectionModule from 'client/store/modules/CollectionModule';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import ValidationErrorsMixin, { addValidationErrorsToState } from 'client/store/mixins/ValidationErrorsMixin';

import * as DynamicGroupsItemAsideReusable from 'client/store/modules/models/reusables/DynamicGroupsItemAsideReusable';

const initialFiltersState = () => [
  { name: 'type', operator: 'eq', value: ItemTypeConsts.userGroup },
];

const userGroupModel = 'UserGroupModel';

export class UserGroupCollectionClass extends CollectionModule {
  /**
   * Extend base class to add User Group specific functionality.
   */
  extractModule() {
    const module = super.extractModule();

    mixin(module.actions, ValidationErrorsMixin.actions);
    mixin(module.getters, ValidationErrorsMixin.getters);
    mixin(module.mutations, ValidationErrorsMixin.mutations);

    module.actions.decorateUserGroups = (...params) => this.decorateUserGroups(...params);
    module.actions.fetchActiveDirectories = (...params) => this.fetchActiveDirectories(...params);
    module.actions.fetchUserGroups = (...params) => this.fetchUserGroups(...params);
    module.actions.fetchDecoratedUserGroups = (...params) => this
      .fetchDecoratedUserGroups(...params);
    module.actions.enableSambaGroup = (...params) => this.enableSambaGroup(...params);
    module.actions.disableSambaGroup = (...params) => this.disableSambaGroup(...params);
    module.actions.fetchUserGroupsById = (...params) => this.fetchUserGroupsById(...params);
    module.actions.resetCollection = (...params) => this.resetCollection(...params);
    module.actions.selectItems = (...params) => this.selectItems(...params);
    module.actions.fetchGroupSummaryResources = (...params) => this
      .fetchGroupSummaryResources(...params);

    module.actions.fetchAndSetGroupsToAddExemption = (...params) => DynamicGroupsItemAsideReusable
      .fetchAndSetGroupsToAddExemption(...params);

    module.getters.getSambaEnabledGroupsToUpdate = (...params) => this
      .getSambaEnabledGroupsToUpdate(...params);
    module.getters.getSambaDisabledGroupsToUpdate = (...params) => this
      .getSambaDisabledGroupsToUpdate(...params);
    module.getters.isManagedByActiveDirectory = (...params) => this
      .isManagedByActiveDirectory(...params);
    module.getters.isSambaEnabled = (...params) => this.isSambaEnabled(...params);

    module.getters.getGroupsToAddExemptionToBind = DynamicGroupsItemAsideReusable
      .getGroupsToAddExemptionToBind;
    module.getters.getGroupsToAddExemptionToUnbind = DynamicGroupsItemAsideReusable
      .getGroupsToAddExemptionToUnbind;
    module.getters.getGroupsToExceedExemptionLimit = DynamicGroupsItemAsideReusable
      .getGroupsToExceedExemptionLimit;

    module.mutations.SET_ACTIVE_DIRECTORIES = (...params) => this.SET_ACTIVE_DIRECTORIES(...params);
    module.mutations.SET_DECORATE_USER_GROUPS = (...params) => this
      .SET_DECORATE_USER_GROUPS(...params);
    module.mutations.ENABLE_SAMBA_GROUP = (...params) => this.ENABLE_SAMBA_GROUP(...params);
    module.mutations.DISABLE_SAMBA_GROUP = (...params) => this.DISABLE_SAMBA_GROUP(...params);
    module.mutations.RESET_FILTERS = (...params) => this.RESET_FILTERS(...params);

    module.mutations.SET_GROUPS_TO_ADD_EXEMPTION = (...params) => DynamicGroupsItemAsideReusable
      .SET_GROUPS_TO_ADD_EXEMPTION(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = addValidationErrorsToState(super.getInitialState());
    state.features.decorateUserGroups = false;
    state.features.enabledSambaGroups = { ids: [] };
    state.features.disabledSambaGroups = { ids: [] };
    state.features.items.filters = initialFiltersState();

    state.features = {
      ...state.features,
      ...DynamicGroupsItemAsideReusable.getState(),
    };

    return state;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * When set to true User Groups will be "decorated" by dispatching the
   * fetchUserGroups action after fetchActiveDirectories. This is used in the
   * UserGroupItemTable when rendered in the Directory Details Aside Panel
   * because each User Group's attributes are required but since they're not
   * available on the list endpoint, each User Group must be fetched uniquely
   * to get them.
   */
  decorateUserGroups({ commit }, value) {
    commit('SET_DECORATE_USER_GROUPS', value);
  }

  /**
   * After fetching the collection make a series of separate requests to GET
   * /api/v2/usergroups/{userGroupId}/activedirectories so that a flag can be
   * set on each item indicating that the user group is managed by Active
   * Directory.
   *
   * These requests will be made _after_ the initial collection is returned
   * so that the icons can be displayed as each request is returned.
   *
   * Refer to the isManagedByActiveDirectory() getter here in this Vuex module
   * to see how this is used.
   */
  async fetchCollection(context, { next = false, populateActiveDirectory = true } = {}) {
    const ids = await super.fetchCollection(context, { next });
    if (populateActiveDirectory && ids) {
      // The Active Directories and User Group details cannot be fetched at
      // the same time because they update the same set of items and race
      // conditions may occur. So fetch the Active Directories first since
      // there may be no work to do there, and then fetch the User Groups,
      // since there is always work to do for that.
      await context.dispatch('fetchActiveDirectories', { ids });
      if (context.state.features.decorateUserGroups) {
        context.dispatch('fetchUserGroups', { ids });
      }
    }

    return ids;
  }

  /**
   * Fetch the Active Directories that may be bound to each User Group. This
   * method returns a promise so that the caller can be notified when it has
   * finished. This is necessary because fetching the Active Directories that
   * are bound to each User Group cannot happen at the same time as fetching
   * the User Group details.
   */
  fetchActiveDirectories(context, { ids }) {
    return new Promise((resolve) => {
      const tasks = [];

      ids.forEach((id) => {
        tasks.push((cb) => {
          this.Api
            .fetchActiveDirectories(id)
            .then((activeDirectories) => {
              context.commit('SET_ACTIVE_DIRECTORIES', { id, activeDirectories });
              cb();
            })
            .catch(() => cb());
        });
      });

      parallelLimit(tasks, 5, () => resolve());
    });
  }

  fetchDecoratedUserGroups(context, { ids }) {
    return new Promise((resolve) => {
      const userGroups = [];
      const tasks = [];
      ids.forEach((id) => {
        tasks.push((cb) => {
          UserGroupApi.fetchUserGroupById(id).then((userGroup) => {
            userGroups.push(userGroup);
            cb();
          }).catch(() => cb());
        });
      });

      parallelLimit(tasks, 5, () => resolve(userGroups));
    });
  }

  /**
   * Fetch each User Group returned above in the fetchCollection action to
   * "decorate" each one with its attributes. This is used in the
   * UserGroupItemTable in the JumpCloud LDAP Directory details aside panel
   * which needs access to each UserGroup's attributes which, unfortunately,
   * are not returned from the list endpoint, i.e. GET /api/v2/usergroups.
   */
  fetchUserGroups({ dispatch }, { ids }) {
    const tasks = [];

    ids.forEach((id) => {
      tasks.push((cb) => {
        dispatch('fetchItem', id)
          .then(() => cb())
          .catch(() => cb());
      });
    });

    parallelLimit(tasks, 5);
  }

  enableSambaGroup({ commit }, id) {
    commit('ENABLE_SAMBA_GROUP', id);
  }

  disableSambaGroup({ commit }, id) {
    commit('DISABLE_SAMBA_GROUP', id);
  }

  fetchUserGroupsById(_, { ids }) {
    const filterValue = ids.join('|');
    return UserGroupApi.fetch(
      0,
      100,
      'name',
      undefined,
      undefined,
      [{ name: 'object_id', operator: 'in', value: filterValue }],
    );
  }

  resetCollection(context, payload) {
    super.resetCollection(context, payload);
    context.dispatch('resetValidationErrors');
  }

  selectItems(context, { ids }) {
    context.commit('SELECT_ITEMS', { ids });
  }

  updateItem(context, payload) {
    let attributes;
    const { id, key, value } = payload;

    if (typeof key !== 'undefined' && typeof value !== 'undefined') {
      attributes = { [key]: value };
    } else {
      ({ attributes } = payload);
    }
    return super.updateItem(context, { id, attributes });
  }

  async fetchGroupSummaryResources({ dispatch }, { id }) {
    const directoryGroupingCollection = 'DirectoryGroupingModule';
    const associationDetails = {
      parentType: ItemTypeAPIs[ItemTypeConsts.userGroup],
      parentId: id,
    };

    const fetchList = [
      dispatch(`${directoryGroupingCollection}/fetchAssociationDetails`, { ...associationDetails, childType: ItemTypeAPIs[ItemTypeConsts.application] }, { root: true }),
      dispatch(`${directoryGroupingCollection}/fetchBoundDirectories`, { ...associationDetails, childType: ItemTypeAPIs[ItemTypeConsts.directory] }, { root: true }),
      dispatch(`${directoryGroupingCollection}/fetchAssociationDetails`, { ...associationDetails, childType: ItemTypeAPIs[ItemTypeConsts.radiusServer] }, { root: true }),
      dispatch(`${userGroupModel}/fetchDeviceGroupItems`, { id }, { root: true }),
      dispatch(`${userGroupModel}/fetchUserGroupItem`, { id }, { root: true }),
    ];

    const [
      application,
      directories,
      radiusServers,
      systemGroups,
      userGroupData,
    ] = await Promise.all(fetchList);

    return {
      application,
      directories,
      radiusServers,
      systemGroups,
      userGroupData,
    };
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  /**
   * Return an array of the User Groups that have had Samba enabled. In order
   * to not modify the state accidentally clones of modified User Groups are
   * returned.
   */
  getSambaEnabledGroupsToUpdate(state) {
    const { boundItems } = state.entities;
    const { enabledSambaGroups } = state.features;

    return enabledSambaGroups.ids.reduce((groupsToAdd, id) => {
      const userGroup = boundItems.byId[id];
      const { name, type } = boundItems.byId[id];

      groupsToAdd.push({
        id: userGroup.id,
        name,
        type,
        attributes: {
          ...userGroup.compiledAttributes,
          ...userGroup.attributes,
          sambaEnabled: true,
        },
      });

      return groupsToAdd;
    }, []);
  }

  /**
   * Return an array of the User Groups that have had Samba disabled. When
   * Samba is disabled the sambaEnabled attribute will be removed, not set to
   * false. And in order to not modify the state accidentally, clones of
   * modified User Groups are returned.
   */
  getSambaDisabledGroupsToUpdate(state) {
    const { boundItems, items } = state.entities;
    const { disabledSambaGroups } = state.features;

    return disabledSambaGroups.ids.reduce((groupsToRemove, id) => {
      // Samba Authentication may be disabled on a User Group that has been
      // unselected. In this case the User Group will be found in the items and
      // not the boundItems. So both collections are checked here.
      const userGroup = boundItems.byId[id] || items.byId[id];
      const { name, type } = userGroup;

      const groupToRemove = {
        id: userGroup.id,
        name,
        type,
        attributes: {
          ...userGroup.compiledAttributes,
          ...userGroup.attributes,
        },
      };

      delete groupToRemove.attributes.sambaEnabled;
      groupsToRemove.push(groupToRemove);

      return groupsToRemove;
    }, []);
  }

  isManagedByActiveDirectory(state) {
    return (id) => {
      const item = state.entities.items.byId[id] || {};
      const { activeDirectories } = item;

      if (activeDirectories && activeDirectories.length) {
        return true;
      }

      return false;
    };
  }

  /**
   * User Groups may be found in either the boundItems or items in the entities.
   *
   * The User Group will be found in the boundItems if it was initially bound
   * to the parent. And the User Group will be found in the items if it was not
   * initially bound to the parent.
   *
   * When the User Group is in the boundItems the compliedAttributes field will
   * be checked for the sambaEnabled attribute.
   *
   * When the Uesr Group is in the items the attributes field will be checked
   * for the sambaEnabled attribute.
   *
   * Inconsistency begets complexity.
   *
   * @todo Keeping track of the items and bound items in two different locations
   * is now proving to be difficult. This must be refactored at some point soon!
   */
  isSambaEnabled(state) {
    return (id) => {
      const boundItem = state.entities.boundItems.byId[id] || {};
      const item = state.entities.items.byId[id] || {};

      const { attributes } = item;
      const { compiledAttributes } = boundItem;

      if (attributes && attributes.sambaEnabled) {
        return true;
      }

      if (compiledAttributes && compiledAttributes.sambaEnabled) {
        return true;
      }

      return false;
    };
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  SET_ACTIVE_DIRECTORIES(state, { id, activeDirectories }) {
    if (state.entities.items.byId[id]) {
      Vue.set(state.entities.items.byId[id], 'activeDirectories', activeDirectories);
    }
  }

  SET_DECORATE_USER_GROUPS(state, value) {
    state.features.decorateUserGroups = value;
  }

  ENABLE_SAMBA_GROUP(state, id) {
    const { disabledSambaGroups, enabledSambaGroups } = state.features;
    const index = disabledSambaGroups.ids.indexOf(id);

    if (index !== -1) {
      disabledSambaGroups.ids.splice(index, 1);
    } else {
      enabledSambaGroups.ids.push(id);
    }

    const { boundItems, items } = state.entities;
    const boundItem = boundItems.byId[id];
    const item = items.byId[id];

    if (boundItem) {
      Vue.set(
        boundItems.byId[id],
        'compiledAttributes',
        { ...boundItem.compiledAttributes, sambaEnabled: true },
      );
    }

    if (item) {
      Vue.set(
        items.byId[id],
        'attributes',
        { ...item.attributes, sambaEnabled: true },
      );
    }
  }

  DISABLE_SAMBA_GROUP(state, id) {
    const { disabledSambaGroups, enabledSambaGroups } = state.features;
    const index = enabledSambaGroups.ids.indexOf(id);

    if (index !== -1) {
      enabledSambaGroups.ids.splice(index, 1);
    } else {
      disabledSambaGroups.ids.push(id);
    }

    const { boundItems, items } = state.entities;
    const boundItem = boundItems.byId[id];
    const item = items.byId[id];

    if (boundItem) {
      const compiledAttributes = { ...boundItem.compiledAttributes };
      delete compiledAttributes.sambaEnabled;
      Vue.set(boundItems.byId[id], 'compiledAttributes', compiledAttributes);
    }

    if (item) {
      const attributes = { ...item.attributes };
      delete attributes.sambaEnabled;
      Vue.set(items.byId[id], 'attributes', attributes);
    }
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  RESET_COLLECTION(state, payload) {
    if (typeof state === 'undefined') return; // For tests where state has not been initialized
    super.RESET_COLLECTION(state, payload);

    if (!payload || (payload && !payload.soft)) {
      const initialState = this.getInitialState();
      const { enabledSambaGroups, disabledSambaGroups } = initialState.features;

      state.features.decorateUserGroups = initialState.features.decorateUserGroups;

      Vue.set(state.features, 'enabledSambaGroups', enabledSambaGroups);
      Vue.set(state.features, 'disabledSambaGroups', disabledSambaGroups);
      Vue.set(state.features.items, 'filters', initialFiltersState());
    }
  }
}

export default new UserGroupCollectionClass(UserGroupApi, 'id', 'name').extractModule();
